import type { Customer } from '@/lib/schemas/db'

export const customerSearchSortKeys = [
  'ref',
  'customer',
  'invoiceTo',
  'updated',
] as const

export type CustomerSearchSortKey = (typeof customerSearchSortKeys)[number]

export interface CustomersSearchFilters {
  /** Substring match on the public ref */
  ref?: string
  /** Substring match on full name or external (Omni) id */
  customer?: string
  /** Substring match on contact email or phone */
  contact?: string
  /** Substring match on the contact address */
  address?: string
  /** Substring match on the invoice name */
  invoiceTo?: string
}

export interface CustomersSearchInput {
  filters?: CustomersSearchFilters
  sort?: { key: CustomerSearchSortKey; dir: 'asc' | 'desc' }
  page: number
  pageSize: number
}

/**
 * Search results come back as ordered customer ids: the matching records are
 * upserted into the adapter's customers collection, so the UI reads them
 * through TanStack DB and uses the keys only for membership + order.
 */
export interface CustomersSearchResult {
  keys: string[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

/** A materialised page of customers, before being upserted + keyed. */
export interface CustomersSearchPage {
  items: Customer[]
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
}

const sortValue: Record<CustomerSearchSortKey, (customer: Customer) => string> = {
  ref: (customer) => customer.publicId ?? customer.id,
  customer: (customer) => customer.fullName.toLowerCase(),
  invoiceTo: (customer) => (customer.invoiceName ?? '').toLowerCase(),
  updated: (customer) => customer.updatedAt,
}

const contains = (value: string | undefined, query: string) =>
  (value ?? '').toLowerCase().includes(query.toLowerCase())

/**
 * In-memory equivalent of the PocketBase customers search, so the in-memory
 * adapter (Storybook/tests) behaves like the server: same filter semantics
 * (`~` substring, multi-field ORs), sort keys and pagination shape.
 */
export function searchCustomerRows(
  rows: Customer[],
  input: CustomersSearchInput,
): CustomersSearchPage {
  const { filters = {}, sort } = input

  const filtered = rows.filter((customer) => {
    if (filters.ref && !contains(customer.publicId ?? customer.id, filters.ref)) {
      return false
    }
    if (
      filters.customer &&
      !contains(customer.fullName, filters.customer) &&
      !contains(customer.externalId, filters.customer)
    ) {
      return false
    }
    if (
      filters.contact &&
      !contains(customer.contactEmail, filters.contact) &&
      !contains(customer.contactPhone, filters.contact)
    ) {
      return false
    }
    if (filters.address && !contains(customer.contactAddress, filters.address)) {
      return false
    }
    if (filters.invoiceTo && !contains(customer.invoiceName, filters.invoiceTo)) {
      return false
    }
    return true
  })

  if (sort) {
    const dir = sort.dir === 'asc' ? 1 : -1
    const value = sortValue[sort.key]
    filtered.sort((a, b) => {
      const av = value(a)
      const bv = value(b)
      return av < bv ? -dir : av > bv ? dir : 0
    })
  }

  const pageSize = Math.max(1, input.pageSize)
  const totalItems = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))
  const page = Math.min(Math.max(1, input.page), totalPages)
  const start = (page - 1) * pageSize

  return {
    items: filtered.slice(start, start + pageSize),
    page,
    pageSize,
    totalItems,
    totalPages,
  }
}
